import type { BaseState } from "../../account-information/types/Common.types";

export type OnboardStep = 1 | 2 | 3;

export interface CountrySelectionFormValues {
  country: string;
}

export interface IAdditionalContactInfo {
  firstName: string;
  lastName: string;
  workEmail: string;
  phoneNumber: string;
  jobTitle: string;
}
export interface StoreFormValues {
  productCategories: string[];
  averageOrdersPerMonth: string;
  isInternationalShipping: boolean;
  destinationCountries: string[];
  isUsing3PL: boolean;
  threePLAccountNumber: string;
  companyName: string;
  firstName: string;
  lastName: string;
  companyRegistrationNumber: string;
  workEmail: string;
  companyAddress: string;
  phoneNumber: string;
  postalCode: string;
  city: string;
  jobTitle: string;
  country: string;
  companyCountry?: string;
  state: string;
  additionalFirstName: string;
  additionalLastName: string;
  additionalWorkEmail: string;
  additionalPhoneNumber: string;
  additionalJobTitle: string;
  additionalContactInformations?: [
    {
      firstName?: string;
      lastName?: string;
      workEmail?: string;
      phoneNumber?: string;
      jobTitle?: string;
    },
  ];
}
export type StoreValueKey = keyof StoreFormValues;

export type StringStoreValueKey = {
  [K in keyof StoreFormValues]: StoreFormValues[K] extends string ? K : never;
}[keyof StoreFormValues];

export interface TermsSectionValues {
  isTermsAndConditionsAccepted: boolean;
  isLetterOfRecordAgreementAccepted: boolean;
  isDataProcessingAgreementAccepted: boolean;
  isProhibitedItemsAccepted: boolean;
}

export interface OnboardFormValues {
  country: string;
  storeValues: StoreFormValues;
  termsValues: { [key: string]: boolean };
}

export interface CommonStepFormProps {
  onNext: () => void;
  onBack?: () => void;
  setFormData: React.Dispatch<React.SetStateAction<OnboardFormValues>>;
  formData: OnboardFormValues;
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CountrySelectionFormProps extends CommonStepFormProps {}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface StoreFormProps extends CommonStepFormProps {}

export interface TermsSectionFormProps extends Omit<
  CommonStepFormProps,
  "onSubmit" | "onNext"
> {
  onBack: () => void;
  handleConfirm: () => void;
}

export interface StepperProps {
  currentStep: OnboardStep;
}

export interface CountryState {
  code: string;
  name: string;
}
export interface Country {
  id: string | number;
  code: string;
  name: string;
  flagUrl?: string;
  states?: CountryState[];
}
export interface ProductCategory {
  id: string | number;
  name: string;
}
export interface Term {
  id: number | string;
  url: string;
  key?: string;
  name: string;
}

export interface Countries extends BaseState {
  data: Country[] | null;
}

export interface States extends BaseState {
  data: CountryState[] | null;
}

interface ProductCategories extends BaseState {
  data: ProductCategory[] | null;
}

export interface DestinationCountries extends BaseState {
  data: Omit<Country, "state">[] | null;
}

interface Terms extends BaseState {
  data: Omit<Term[], "key" | "title"> | null;
  lastActiveFocusTerm?: Term | null;
}

export interface CompanyDetailsApiResponse {
  companyName: string;
  companyAddress: string;
  city: string;
  state: string;
  countryCode: string;
  postalCode: string;
  workEmail: string;
}

export interface CompanyDetails extends BaseState {
  data: CompanyDetailsApiResponse | null;
}

export interface OnboardingState extends BaseState {
  sourceCountries: Countries;
  states: States;
  productCategories: ProductCategories;
  destinationCountries: DestinationCountries;
  countries: Countries;
  terms: Terms;
  companyDetails: CompanyDetails;
}

export const initialState: OnboardingState = {
  sourceCountries: {
    data: null,
    status: "idle",
    error: null,
  },
  states: {
    data: null,
    status: "idle",
    error: null,
  },
  productCategories: {
    data: null,
    status: "idle",
    error: null,
  },
  countries: {
    data: null,
    status: "idle",
    error: null,
  },
  destinationCountries: {
    data: null,
    status: "idle",
    error: null,
  },
  terms: {
    data: null,
    status: "idle",
    error: null,
    lastActiveFocusTerm: null,
  },
  companyDetails: {
    data: null,
    status: "idle",
    error: null,
  },
  status: "idle",
  error: null,
};

export interface SubmitOnboardingArgs {
  payload: StoreFormValues;
  useDummyToken?: boolean;
  _signal?: AbortSignal;
}
